import { FormEventHandler, useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { Toast } from "@sscs/ui";
import { apiErrorToast, validatePassword } from "Utils";
import * as yup from "Utils/yup";
import { useChangePasswordMutation } from "../services/userSettings.api";

type ChangePasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type UseChangePasswordForm = {
  errors: FieldErrors<ChangePasswordFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  methods: UseFormReturn<ChangePasswordFormData>;
};

export const ChangePasswordFormSchema: yup.SchemaOf<ChangePasswordFormData> =
  yup.object({
    currentPassword: yup.string().required("This is a required field"),
    newPassword: validatePassword().required(),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], "Passwords must match")
      .required("This is a required field"),
  });

const useChangePasswordForm = (): UseChangePasswordForm => {
  const defaultValues = useMemo(
    () => ({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    }),
    [],
  );

  const [changePassword] = useChangePasswordMutation();

  const methods = useForm<ChangePasswordFormData>({
    resolver: yupResolver(ChangePasswordFormSchema),
    defaultValues,
  });

  const {
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
    reset,
  } = methods;

  // to reset after a submit, react-hook-form recommend doing so inside a useEffect
  // https://react-hook-form.com/api/useform/reset/
  useEffect(() => {
    reset(defaultValues);
  }, [isSubmitSuccessful, reset, defaultValues]);

  const onSubmit: SubmitHandler<ChangePasswordFormData> = async (data) => {
    try {
      await changePassword({
        body: {
          current_password: data.currentPassword,
          new_password: data.newPassword,
        },
      }).unwrap();

      Toast.show({
        title: "Password changed",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not change password");
    }
    reset(defaultValues);
  };

  return {
    errors,
    methods,
    onSubmit: handleSubmit(onSubmit),
  };
};

export default useChangePasswordForm;
